import Lottie from 'lottie-react'
import {Modal, Spinner, Button, Form, Badge} from 'react-bootstrap'
import searchLottie from '../../assets/lotties/search-leads.json'
import {IResContactsPreviewAPI, PolicySummary, MemberPolicySummary} from '../../types/contactsApi'
import React, {CSSProperties} from 'react'
import {hasPermission} from 'app/tfi/helpers'
import {ALL_PERMISSIONS, getPaymentVariant, getBadgeTextColor} from 'app/tfi/helpers/consts'
import {useGetUser} from 'app/tfi/store/userStore'
import {useMutation, useQuery} from '@apollo/client'
import {REQUEST_POLICY_ASSIGNMENT} from '../../gql/mutations/policyMutation'
import {IRequestPolicyAssignmentResponse, AllPolicyStatusesData} from '../../types/policies'
import {toast} from 'react-toastify'
import {parseId} from '../../helpers'
import {BadgeStatePolicy} from '../../pages/onePolicy/components/BadgeStatePolicy'
import {GET_ALL_POLICIES_STATUSES} from '../../gql/queries/policiesQueries'
import {getColorByStatus, getTitleByStatus} from '../../pages/shared/utils'
import {defineMessages, useIntl} from 'react-intl'
import {SEARCH_MESSAGES as S} from './searchMessages'
import {paymentStatusLabel} from '../../helpers/catalogMessages'

const M = defineMessages({
  contactHint: {
    id: 'shell.search.hint.contact',
    defaultMessage: 'You can find your Contacts by entering phone number or name',
  },
  notAvailable: {id: 'common.value.notAvailable', defaultMessage: 'N/A'},
  roleHolder: {id: 'shell.search.role.holder', defaultMessage: 'Holder'},
  roleFamily: {id: 'shell.search.role.family', defaultMessage: 'Family'},
  archived: {id: 'shell.search.badge.archived', defaultMessage: 'Archived'},

  // Table
  toggleColumn: {
    id: 'shell.search.col.toggleYear',
    defaultMessage: '{shown, select, true {Hide} other {Show}} {year} column',
  },
  colFullName: {id: 'shell.search.col.fullName', defaultMessage: 'Full Name'},
  colAge: {id: 'shell.search.col.age', defaultMessage: 'Age'},
  colAgent: {id: 'common.field.agent', defaultMessage: 'Agent'},
  colAssignedTo: {id: 'shell.search.col.assignedTo', defaultMessage: 'Assigned To'},
  colActivePolicy: {id: 'shell.search.col.activePolicy', defaultMessage: 'Active Policy'},
  colPolicyYear: {id: 'shell.search.col.policyYear', defaultMessage: 'Policy {year}'},
  colPolicySubtitle: {
    id: 'shell.search.col.policySubtitle',
    defaultMessage: 'Type · Status · Payment',
  },
  colState: {id: 'common.field.state', defaultMessage: 'State'},
  colActions: {id: 'common.field.actions', defaultMessage: 'Actions'},
  yes: {id: 'common.boolean.yes', defaultMessage: 'Yes'},
  no: {id: 'common.boolean.no', defaultMessage: 'No'},

  // Assignment request
  requestAssign: {id: 'shell.search.request.action', defaultMessage: 'Request assign to me'},
  requestTitle: {id: 'shell.search.request.title', defaultMessage: 'Request assignment'},
  requestBody: {
    id: 'shell.search.request.body',
    defaultMessage:
      'Do you want to request that <b>{policy}</b> be assigned to you? Current assigned: {assigned}.',
  },
  requestUnknown: {id: 'shell.search.request.unknown', defaultMessage: 'Unknown'},
  requestReason: {id: 'shell.search.request.reason', defaultMessage: 'Reason (optional)'},
  requestNotePlaceholder: {
    id: 'shell.search.request.notePlaceholder',
    defaultMessage: 'Add a note for your supervisor',
  },
  cancel: {id: 'common.action.cancel', defaultMessage: 'Cancel'},
  sending: {id: 'shell.search.request.sending', defaultMessage: 'Sending…'},
  sendRequest: {id: 'shell.search.request.send', defaultMessage: 'Send request'},
  requestSent: {id: 'shell.search.request.sent', defaultMessage: 'Assignment request sent'},
  requestFailed: {
    id: 'shell.search.request.failed',
    defaultMessage: 'The request could not be sent, please try again later',
  },
})

const EMPTY_VALUE = '-'
// Points at the policy holder a family member belongs to — design, not text.
const NESTED_ARROW = '↳'

export const ModalBodySearchContactHeader: React.FC<{
  data: undefined | IResContactsPreviewAPI
  loading: boolean
  keyword: string
  onClose: () => void
}> = ({data, loading, onClose, keyword}) => {
  const intl = useIntl()
  const permissions = useGetUser((user) => user?.user?.permissions)
  const userGroups = useGetUser((user) => user?.user?.groups) || []
  const currentUser = useGetUser((user) => user?.user?.user)
  const currentUserId = currentUser?.id ? parseInt(`${currentUser.id}`) : null
  const isAsesor = userGroups.includes('Asesor')
  const isAgente = userGroups.includes('Agente')
  const canViewAllPolicyStatuses =
    !!permissions && hasPermission(permissions, ALL_PERMISSIONS.VIEW_POLICY_STATUS)
  const isRestrictedPolicyStatus = isAgente && !canViewAllPolicyStatuses

  const [requestContext, setRequestContext] = React.useState<{
    policyId: string
    policyName: string
    assignedName: string | null
    contactName: string
  } | null>(null)
  const [requestReason, setRequestReason] = React.useState('')
  const [showExtraYear, setShowExtraYear] = React.useState(false)
  const bodyRef = React.useRef<HTMLDivElement | null>(null)

  const [requestPolicyAssignment, {loading: requestingAssignment}] =
    useMutation<IRequestPolicyAssignmentResponse>(REQUEST_POLICY_ASSIGNMENT)
  const {data: policyStatusesData} = useQuery<AllPolicyStatusesData>(GET_ALL_POLICIES_STATUSES, {
    variables: {first: 100},
  })

  React.useEffect(() => {
    const dialog = bodyRef.current?.closest('.modal-dialog') as HTMLElement | null
    if (dialog) {
      dialog.style.setProperty('--bs-modal-width', '95vw')
      dialog.style.maxWidth = '95vw'
      dialog.style.width = '95vw'
    }
  }, [])

  if (!permissions) return null

  const highlightKeyword = (text: string, color: CSSProperties['color'] = 'yellow') => {
    if (!keyword) return text
    const escapedKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    // Usamos una expresión regular para encontrar todas las ocurrencias de la palabra clave
    const regex = new RegExp(`(${escapedKeyword})`, 'gi')
    // Reemplazamos las ocurrencias con la palabra clave envuelta en un span con un estilo de fondo amarillo
    return text.replace(regex, `<span style="background-color: ${color};">$1</span>`)
  }

  if (loading) {
    return (
      <Modal.Body style={{minHeight: '300px'}}>
        <div className='d-flex align-items-center justify-content-center w-100 h-100'>
          <Spinner animation='border' />
        </div>
      </Modal.Body>
    )
  }
  if (!data) {
    return (
      <Modal.Body
        style={{minHeight: '300px'}}
        className='d-flex align-items-center justify-content-center gap-5 flex-column'
      >
        {intl.formatMessage(M.contactHint)}
        <Lottie animationData={searchLottie} autoPlay />
      </Modal.Body>
    )
  }
  const getPolicyTimestamp = (policy: PolicySummary): number => {
    const effective = policy.effectiveDate ? Date.parse(policy.effectiveDate) : NaN
    const created = policy.created ? Date.parse(policy.created) : NaN
    const timestamps = [effective, created].filter((value) => !Number.isNaN(value))
    return timestamps.length > 0 ? Math.max(...timestamps) : Number.NEGATIVE_INFINITY
  }

  const collectPolicy = (member?: MemberPolicySummary | null): PolicySummary | null => {
    if (!member?.policy || !member.policy.id) return null
    return member.policy
  }

  const collectPolicies = (
    node: IResContactsPreviewAPI['allContactsPreview']['edges'][number]['node']
  ): PolicySummary[] => {
    const policyMap = new Map<string, PolicySummary>()
    const primaryPolicy = collectPolicy(node.memberPolicy)
    if (primaryPolicy) policyMap.set(primaryPolicy.id, primaryPolicy)

    node.members?.edges?.forEach(({node: memberNode}) => {
      const memberPolicy = collectPolicy(memberNode)
      if (memberPolicy) policyMap.set(memberPolicy.id, memberPolicy)
    })

    return Array.from(policyMap.values())
  }

  const getLatestPolicy = (
    node: IResContactsPreviewAPI['allContactsPreview']['edges'][number]['node']
  ): PolicySummary | null => {
    const policies = collectPolicies(node)
    if (policies.length === 0) return null

    return policies.sort((a, b) => getPolicyTimestamp(b) - getPolicyTimestamp(a)).at(0) || null
  }

  const formatUserName = (user?: {firstName: string | null; lastName: string | null} | null) => {
    if (!user) return ''
    return `${user.firstName || ''} ${user.lastName || ''}`.trim()
  }

  const isCancelledPolicy = (policy?: PolicySummary | null): boolean => {
    const status = policy?.status || ''
    return status.toUpperCase().includes('CANCEL')
  }

  const isPolicyActive = (policy: PolicySummary, now: Date): boolean => {
    if (isCancelledPolicy(policy)) return false
    if (!policy.effectiveDate) return false

    const start = new Date(policy.effectiveDate)
    if (Number.isNaN(start.getTime())) return false

    const end = new Date(start.getTime())
    end.setUTCFullYear(end.getUTCFullYear() + 1)

    return now >= start && now < end
  }

  // Obtiene todas las pólizas para el año indicado, una por tipo (la más reciente de cada tipo)
  const getPoliciesForYear = (
    node: IResContactsPreviewAPI['allContactsPreview']['edges'][number]['node'],
    targetYear: number
  ): PolicySummary[] => {
    const allPolicies = collectPolicies(node).filter((policy) => {
      if (!policy.effectiveDate) return false
      const effectiveYear = new Date(policy.effectiveDate).getUTCFullYear()
      if (effectiveYear !== targetYear) return false
      return true
    })

    const byType = new Map<string, PolicySummary>()
    for (const policy of allPolicies) {
      const baseType = policy.typePolicy?.typePolicy || policy.type || 'Other'
      const typeKey = isCancelledPolicy(policy) ? `${baseType}_cancelled` : baseType
      const existing = byType.get(typeKey)
      if (!existing || getPolicyTimestamp(policy) > getPolicyTimestamp(existing)) {
        byType.set(typeKey, policy)
      }
    }

    return Array.from(byType.values()).sort((a, b) => {
      const ta = a.typePolicy?.typePolicy || a.type || 'Other'
      const tb = b.typePolicy?.typePolicy || b.type || 'Other'
      return ta.localeCompare(tb)
    })
  }

  const getPaymentStatusLabel = (value?: string | null) =>
    paymentStatusLabel(intl, value) || EMPTY_VALUE

  const now = new Date()
  const currentYear = now.getUTCFullYear()
  const currentMonth = now.getMonth() // 0-11 (0 = Enero, 9 = Octubre)
  const previousYear = currentYear - 1
  const nextYear = currentYear + 1

  // De octubre a diciembre: mostrar año actual y siguiente (oculto: año anterior)
  // De enero a septiembre: mostrar año anterior y actual (oculto: año siguiente)
  const isOctoberToDecember = currentMonth >= 9
  const firstYearToShow = isOctoberToDecember ? currentYear : previousYear
  const secondYearToShow = isOctoberToDecember ? nextYear : currentYear

  // El año extra que se puede mostrar/ocultar con el toggle
  const extraYearToShow = isOctoberToDecember ? previousYear : nextYear

  // Toma la póliza más reciente (por fecha effective/created) que sea tipo POLICY y no cancelada
  const getLatestActivePolicy = (
    node: IResContactsPreviewAPI['allContactsPreview']['edges'][number]['node'],
    now: Date
  ): PolicySummary | null => {
    return (
      collectPolicies(node)
        .filter((policy) => policy.type === 'POLICY' && isPolicyActive(policy, now))
        .sort((a, b) => getPolicyTimestamp(b) - getPolicyTimestamp(a))
        .at(0) || null
    )
  }

  const buildRolMap = (
    node: IResContactsPreviewAPI['allContactsPreview']['edges'][number]['node']
  ): Map<string, string> => {
    const map = new Map<string, string>()
    if (node.memberPolicy?.policy?.id && node.memberPolicy.rol) {
      map.set(node.memberPolicy.policy.id, node.memberPolicy.rol)
    }
    node.members?.edges?.forEach(({node: m}) => {
      if (m.policy?.id && m.rol) map.set(m.policy.id, m.rol)
    })
    return map
  }

  const getRolSummary = (rolMap: Map<string, string>): 'HOLDER' | 'FAMILY' | 'MIXED' | null => {
    if (rolMap.size === 0) return null
    const hasHolder = Array.from(rolMap.values()).some((r) => r === 'HOLDER')
    const hasFamily = Array.from(rolMap.values()).some((r) => r !== 'HOLDER')
    if (hasHolder && hasFamily) return 'MIXED'
    // i18n-ok: valores del enum de rol, no interfaz; la etiqueta se pinta con M.roleHolder / M.roleFamily
    return hasHolder ? 'HOLDER' : 'FAMILY'
  }

  const statusEdges = policyStatusesData?.allPolicyStatuses?.edges ?? []
  const visibleStatusEdges = isRestrictedPolicyStatus
    ? statusEdges.filter(({node}) => node.enabledForAgents)
    : statusEdges
  const getStatusColor = (policy?: PolicySummary | null) => {
    return getColorByStatus(visibleStatusEdges, policy?.status || '')
  }
  const canShowPolicyStatus = (statusCode: string) => {
    if (!statusCode) return false
    if (!isRestrictedPolicyStatus) return true
    return visibleStatusEdges.some(({node}) => node.code === statusCode)
  }

  const renderPoliciesByType = (
    policies: PolicySummary[],
    mutedText?: string,
    rolMap?: Map<string, string>
  ) => {
    if (policies.length === 0) {
      return mutedText ? <span className='badge badge-secondary'>{mutedText}</span> : '-'
    }

    return (
      <div className='d-flex flex-column gap-1'>
        {policies.map((policy) => {
          const typeName = policy.typePolicy?.typePolicy || policy.type || 'Policy'
          const isArchivedPolicy = !!policy.archivedDate || policy.isArchived === true
          const policyRol = rolMap?.get(policy.id)
          return (
            <div key={policy.id} className='d-flex align-items-center gap-1 flex-wrap'>
              <Badge bg='secondary' style={{fontSize: '0.75rem', minWidth: '38px', textAlign: 'center'}}>
                {typeName}
              </Badge>
              {canShowPolicyStatus(policy.status || '') && (
                <BadgeStatePolicy
                  status={getTitleByStatus(visibleStatusEdges, policy.status || '')}
                  color={getStatusColor(policy)}
                />
              )}
              <Badge className='badge-sm' bg={getPaymentVariant(policy.paymentStatus || undefined)} text={getBadgeTextColor(getPaymentVariant(policy.paymentStatus || undefined))}>
                {policy.paymentStatus
                  ? paymentStatusLabel(intl, policy.paymentStatus)
                  : intl.formatMessage(M.notAvailable)}
              </Badge>
              {policyRol && (
                <Badge
                  bg={policyRol === 'HOLDER' ? 'primary' : 'warning'}
                  style={{fontSize: '0.6rem', color: policyRol === 'HOLDER' ? '#fff' : '#000'}}
                >
                  {intl.formatMessage(policyRol === 'HOLDER' ? M.roleHolder : M.roleFamily)}
                </Badge>
              )}
              {isArchivedPolicy && (
                <Badge bg='dark' style={{color: '#fff'}}>
                  {intl.formatMessage(M.archived)}
                </Badge>
              )}
            </div>
          )
        })}
      </div>
    )
  }

  const handleOpenRequestModal = (
    policy: PolicySummary,
    assignedName: string | null,
    contactName: string
  ) => {
    setRequestContext({
      policyId: policy.id,
      policyName: policy.policyName || `Policy #${parseId(policy.id)}`,
      assignedName,
      contactName,
    })
    setRequestReason('')
  }

  const handleSubmitRequest = async () => {
    if (!requestContext) return
    try {
      const {data: mutationData} = await requestPolicyAssignment({
        variables: {
          policyId: parseId(requestContext.policyId),
          reason: requestReason.trim() === '' ? null : requestReason.trim(),
        },
      })

      const payload = mutationData?.requestPolicyAssignment
      if (payload?.success) {
        toast.success(payload.message || intl.formatMessage(M.requestSent))
        setRequestContext(null)
        return
      }

      const errorMessage =
        payload?.errors?.[0]?.message || intl.formatMessage(M.requestFailed)
      toast.error(errorMessage)
    } catch (err: any) {
      toast.error(err?.message || intl.formatMessage(M.requestFailed))
    }
  }

  return (
    <Modal.Body
      ref={bodyRef}
      style={{minHeight: '300px', maxHeight: '70vh', overflowY: 'auto', width: '100%'}}
      className='w-100'
    >
      <div
        className='table-responsive'
        style={{width: '100%', maxHeight: '60vh', overflow: 'auto', minWidth: '1300px'}}
      >
        <table className='table table-striped align-middle'>
          <thead className='bg-white sticky-top' style={{top: 0, zIndex: 1}}>
            <tr>
              <th colSpan={100} className='border-0 pb-2'>
                <div className='d-flex justify-content-end'>
                  <Button
                    variant='outline-secondary'
                    size='sm'
                    onClick={() => setShowExtraYear(!showExtraYear)}
                  >
                    <i className={`bi ${showExtraYear ? 'bi-eye-slash' : 'bi-eye'} me-1`} />
                    {intl.formatMessage(M.toggleColumn, {shown: String(showExtraYear), year: extraYearToShow})}
                  </Button>
                </div>
              </th>
            </tr>
            <tr>
              <th>{intl.formatMessage(M.colFullName)}</th>
              <th>{intl.formatMessage(M.colAge)}</th>
              <th>{intl.formatMessage(M.colAgent)}</th>
              {hasPermission(permissions, ALL_PERMISSIONS.VIEW_CONTACT_PREVIEW_ASSIGNED) && (
                <th>{intl.formatMessage(M.colAssignedTo)}</th>
              )}
              <th>{intl.formatMessage(M.colActivePolicy)}</th>
              {showExtraYear && (
                <th>
                  {intl.formatMessage(M.colPolicyYear, {year: extraYearToShow})}
                  <div className='text-muted fw-normal' style={{fontSize: '0.90rem'}}>
                    {intl.formatMessage(M.colPolicySubtitle)}
                  </div>
                </th>
              )}
              <th>
                {intl.formatMessage(M.colPolicyYear, {year: firstYearToShow})}
                <div className='text-muted fw-normal' style={{fontSize: '0.90rem'}}>
                  {intl.formatMessage(M.colPolicySubtitle)}
                </div>
              </th>
              <th>
                {intl.formatMessage(M.colPolicyYear, {year: secondYearToShow})}
                <div className='text-muted fw-normal' style={{fontSize: '0.90rem'}}>
                  {intl.formatMessage(M.colPolicySubtitle)}
                </div>
              </th>
              <th>{intl.formatMessage(M.colState)}</th>
              {isAsesor && (
                <th className='text-end' style={{width: '220px'}}>
                  {intl.formatMessage(M.colActions)}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {data.allContactsPreview.edges.map(({node}) => {
              const calculateAge = (dateBirth?: string | null): number | string => {
                if (!dateBirth) return '-'
                const birth = new Date(dateBirth)
                const diffMs = Date.now() - birth.getTime()
                const ageDt = new Date(diffMs)
                return Math.abs(ageDt.getUTCFullYear() - 1970)
              }

              const latestPolicy = getLatestPolicy(node)
              const agentName = latestPolicy?.user ? formatUserName(latestPolicy.user) : '-'
              const assignedName = latestPolicy?.assignedTo
                ? formatUserName(latestPolicy.assignedTo)
                : '-'

              const displayName =
                node.fullName ||
                `${node.firstName || ''} ${node.lastName || ''}`.replace(/\s+/g, ' ').trim() ||
                'Unnamed contact'
              const highlightedName = highlightKeyword(displayName)

              const extraYearPolicies = showExtraYear ? getPoliciesForYear(node, extraYearToShow) : []
              const firstYearPolicies = getPoliciesForYear(node, firstYearToShow)
              const secondYearPolicies = getPoliciesForYear(node, secondYearToShow)
              const activePolicy = getLatestActivePolicy(node, now)
              const hasActivePolicy = !!activePolicy

              const rolMap = buildRolMap(node)
              const rolSummary = getRolSummary(rolMap)
              const isMixed = rolSummary === 'MIXED'
              const holderName =
                rolSummary === 'FAMILY'
                  ? (collectPolicies(node).find((p) => p.policyName)?.policyName ?? null)
                  : null

              return (
                <tr key={node.id}>
                  <td>
                    <div className='d-flex flex-column align-items-start gap-1'>
                      <span dangerouslySetInnerHTML={{__html: highlightedName}} />
                      {!isMixed && rolSummary && (
                        <Badge
                          bg={rolSummary === 'HOLDER' ? 'primary' : 'warning'}
                          style={{fontSize: '0.6rem', color: rolSummary === 'HOLDER' ? '#fff' : '#000'}}
                        >
                          {intl.formatMessage(rolSummary === 'HOLDER' ? M.roleHolder : M.roleFamily)}
                        </Badge>
                      )}
                      {holderName && (
                        <span
                          style={{fontSize: '0.80rem', paddingLeft: '10px', color: '#444'}}
                        >
                          {NESTED_ARROW} {holderName}
                        </span>
                      )}
                    </div>
                  </td>
                  <td>{calculateAge(node.dateBirth)}</td>
                  <td>{agentName || EMPTY_VALUE}</td>
                  {hasPermission(permissions, ALL_PERMISSIONS.VIEW_CONTACT_PREVIEW_ASSIGNED) && (
                    <td>{assignedName || EMPTY_VALUE}</td>
                  )}
                  <td>
                    {hasActivePolicy ? (
                      <span className='badge badge-success'>{intl.formatMessage(M.yes)}</span>
                    ) : (
                      <span className='badge badge-secondary'>{intl.formatMessage(M.no)}</span>
                    )}
                  </td>
                  {showExtraYear && (
                    <td>{renderPoliciesByType(extraYearPolicies, 'No', isMixed ? rolMap : undefined)}</td>
                  )}
                  <td>{renderPoliciesByType(firstYearPolicies, 'No', isMixed ? rolMap : undefined)}</td>
                  <td>{renderPoliciesByType(secondYearPolicies, 'No', isMixed ? rolMap : undefined)}</td>
                  <td>{node.stateEU?.name}</td>
                  {isAsesor && (
                    <td className='text-end'>
                      {(() => {
                        const assignedId = latestPolicy?.assignedTo?.id
                        const assignedNumericId = assignedId
                          ? parseInt(`${parseId(assignedId)}`)
                          : null
                        const canRequestAssignment =
                          isAsesor &&
                          !!latestPolicy &&
                          !!latestPolicy.effectiveDate &&
                          !!assignedNumericId &&
                          !!currentUserId &&
                          assignedNumericId !== currentUserId

                        if (!canRequestAssignment) return null

                        return (
                          <Button
                            variant='primary'
                            size='sm'
                            onClick={() =>
                              handleOpenRequestModal(
                                latestPolicy,
                                assignedName && assignedName !== '-' ? assignedName : null,
                                displayName
                              )
                            }
                          >
                            {intl.formatMessage(M.requestAssign)}
                          </Button>
                        )
                      })()}
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <Modal show={!!requestContext} onHide={() => setRequestContext(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{intl.formatMessage(M.requestTitle)}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {requestContext && (
            <>
              <p className='mb-3'>
                {intl.formatMessage(M.requestBody, {
                  policy: requestContext.policyName,
                  assigned:
                    requestContext.assignedName || intl.formatMessage(M.requestUnknown),
                  b: (chunks: (string | JSX.Element)[]) => <strong>{chunks}</strong>,
                })}
              </p>
              <Form.Group className='mb-3'>
                <Form.Label>{intl.formatMessage(M.requestReason)}</Form.Label>
                <Form.Control
                  as='textarea'
                  rows={3}
                  value={requestReason}
                  onChange={(e) => setRequestReason(e.target.value)}
                  placeholder={intl.formatMessage(M.requestNotePlaceholder)}
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setRequestContext(null)}>
            {intl.formatMessage(M.cancel)}
          </Button>
          <Button variant='primary' onClick={handleSubmitRequest} disabled={requestingAssignment}>
            {intl.formatMessage(requestingAssignment ? M.sending : M.sendRequest)}
          </Button>
        </Modal.Footer>
      </Modal>
    </Modal.Body>
  )
}
